import { useCameras } from '@hakimo-ui/hakimo/data-access';
import { CamFilterValues } from '@hakimo-ui/hakimo/types';
import {
  useDebounce,
  useExportCsv,
  useIsSupportRole,
} from '@hakimo-ui/hakimo/util';
import { Button, HakimoSpinner, InputField } from '@hakimo-ui/shared/ui-base';
import { ArrowDownTrayIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { getFormattedExportData, getSearchParams } from './util';

export type CameraListView = 'list' | 'groups';
export interface Props {
  isDefault: boolean;
  isLoading: boolean;
  onClickOpenFilterPanel: () => void;
  onSearch: (value: string) => void;
  camFilters: CamFilterValues;
  isDataValid: boolean;
  view: CameraListView;
  onViewChange: (view: CameraListView) => void;
  canViewCameraGroup: boolean;
}

export function CameraListHeader(props: Props) {
  const {
    isLoading,
    onClickOpenFilterPanel,
    onSearch,
    isDefault,
    camFilters,
    isDataValid,
    view,
    onViewChange,
    canViewCameraGroup,
  } = props;
  const [isExporting, setIsExporting] = useState(false);
  const [searchCameraValue, setSearchCameraValue] = useState('');
  const debouncedCameraValue = useDebounce(searchCameraValue, 400);
  useEffect(() => {
    onSearch(debouncedCameraValue);
  }, [debouncedCameraValue, onSearch]);

  const exportSearchParams = getSearchParams(camFilters);
  const { refetch: fetchExportData } = useCameras(exportSearchParams, false);

  const { exportCsv } = useExportCsv();

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const { data } = await fetchExportData();
      if (!data?.items) {
        throw new Error('Data Fetching Failed');
      }

      const formattedData = getFormattedExportData(data.items);
      if (!Array.isArray(formattedData) || formattedData.length === 0) {
        throw new Error('Formatted data is invalid or empty');
      }
      exportCsv({
        formattedData,
        fileName: 'hakimo-cameras-export.csv',
        fileType: 'csv',
      });
    } catch (error) {
      throw new Error(
        `Export Failed: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    } finally {
      setIsExporting(false);
    }
  };

  const onSearchValChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchCameraValue(e.target.value);
  };

  const isSupportRole = useIsSupportRole();
  const DisableExport = !isDataValid || !isSupportRole;

  const searchPlaceholder =
    view === 'list' ? 'Search cameras...' : 'Search camera groups...';

  return (
    <div className="flex flex-shrink-0 flex-col gap-3 px-4 pt-1">
      <InputField
        label=""
        onChange={onSearchValChange}
        value={searchCameraValue}
        type="search"
        placeholder={searchPlaceholder}
      />

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant={view === 'list' ? 'primary' : 'outline'}
            onClick={() => onViewChange('list')}
            className="!rounded-full"
          >
            List
          </Button>

          {canViewCameraGroup && (
            <Button
              variant={view === 'groups' ? 'primary' : 'outline'}
              onClick={() => onViewChange('groups')}
              className="!rounded-full"
            >
              Groups
            </Button>
          )}
        </div>
        <div className="flex items-center gap-1">
          {isLoading && <HakimoSpinner />}
          {view === 'list' && (
            <Button
              variant="icon"
              onClick={handleExport}
              loading={isExporting}
              title="Download Data"
              disabled={DisableExport}
            >
              <ArrowDownTrayIcon className="w-5" />
            </Button>
          )}

          <Button
            variant="icon"
            onClick={onClickOpenFilterPanel}
            badge={!isDefault}
          >
            <FunnelIcon className="dark:text-ondark-text-2 h-5 w-5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default CameraListHeader;
